import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const InteractiveCalendar = ({
  startDate,
  endDate,
  hoverDate,
  onDateClick,
  onDateHover,
  workDays = [0, 1, 2, 3, 4],
  existingRequests = [],
}) => {
  const [baseMonth, setBaseMonth] = useState(new Date());

  const getDaysInMonth = (year, month) => {
    const date = new Date(year, month, 1);
    const result = [];
    // Skip to Monday start
    const firstDayIndex = (date.getDay() + 6) % 7;
    for (let i = 0; i < firstDayIndex; i++) {
      result.push(null);
    }
    while (date.getMonth() === month) {
      result.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return result;
  };

  const getMonthNames = () => [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const handlePrev = (e) => {
    e.preventDefault();
    setBaseMonth(new Date(baseMonth.getFullYear(), baseMonth.getMonth() - 1, 1));
  };

  const handleNext = (e) => {
    e.preventDefault();
    setBaseMonth(new Date(baseMonth.getFullYear(), baseMonth.getMonth() + 1, 1));
  };

  // JS getDay(): 0=Sun,1=Mon,...,6=Sat → our format: 0=Mon,...,6=Sun
  const isNonWorkDay = (date) => {
    const ourDay = (date.getDay() + 6) % 7;
    return !workDays.includes(ourDay);
  };

  const formatDateStr = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const renderMonth = (monthOffset, hiddenClasses = '') => {
    const targetDate = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + monthOffset, 1);
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const days = getDaysInMonth(year, month);
    const monthName = getMonthNames()[month];

    return (
      <div key={monthOffset} className={`flex-1 min-w-0 bg-white rounded-xl shadow-sm border border-gray-100 p-4 ${hiddenClasses}`}>
        <h3 className="text-md font-bold text-gray-800 mb-4">{monthName} {year}</h3>
        <div className="grid grid-cols-7 gap-y-1 text-center text-xs w-full">
          {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => (
            <div key={i} className="text-gray-400 font-semibold mb-2">{d}</div>
          ))}
          {days.map((date, idx) => {
            if (!date) return <div key={`empty-${idx}`} className="aspect-square w-full max-w-[2rem] mx-auto" />;
            
            const dateStr = formatDateStr(date);
            const weekend = isNonWorkDay(date);
            const isSelectedStart = startDate === dateStr;
            const isSelectedEnd = endDate === dateStr;
            
            let inBand = false;
            let isPreview = false;
            
            if (startDate && endDate) {
               if (dateStr >= startDate && dateStr <= endDate) inBand = true;
            } else if (startDate && hoverDate && !endDate) {
               const min = startDate < hoverDate ? startDate : hoverDate;
               const max = startDate > hoverDate ? startDate : hoverDate;
               if (dateStr >= min && dateStr <= max) isPreview = true;
            }

            let isActiveNode = isSelectedStart || isSelectedEnd;
            let dotColor = '';
            
            // Check for existing requests in this date
            const existingReq = existingRequests.find(req => {
               if (req.status === 'cancelled') return false;
               const start = typeof req.start_datetime === 'string' ? req.start_datetime.slice(0, 10) : req.start_date;
               const end = typeof req.end_datetime === 'string' ? req.end_datetime.slice(0, 10) : req.end_date;
               if (dateStr < start) return false;
               const isReturnConvention = req.end_datetime && /T00:00:00/.test(req.end_datetime);
               return isReturnConvention ? dateStr < end : dateStr <= end;
            });
            
            if (existingReq && !isActiveNode && !inBand && !isPreview) {
               if (existingReq.status === 'approved') dotColor = 'bg-emerald-500';
               else if (existingReq.status === 'pending') dotColor = 'bg-orange-500';
               else if (existingReq.status === 'rejected') dotColor = 'bg-red-500';
            }
            
            let finalClass = 'relative aspect-square w-full max-w-[2rem] mx-auto flex flex-col items-center justify-center cursor-pointer transition-colors rounded-full ';
            
            // Hatched layout on weekend leaves background-color transparent to let highlight shine through
            if (weekend) finalClass += 'bg-hatched-gray ';
            
            if (isActiveNode) {
               finalClass += 'bg-orange-400 text-white font-bold shadow-md z-10';
            } else if (inBand) {
               finalClass += 'calendar-selected-band text-orange-900 font-medium';
            } else if (isPreview) {
               finalClass += 'bg-orange-100 opacity-80 text-orange-800';
            } else if (existingReq) {
               finalClass += weekend ? 'bg-gray-50/50 text-gray-500' : 'bg-gray-50 text-gray-700';
            } else {
               finalClass += weekend ? 'text-gray-400' : 'hover:bg-gray-100 text-gray-700';
            }

            return (
              <div 
                key={dateStr}
                className={finalClass}
                onMouseEnter={() => onDateHover(dateStr)}
                onClick={() => onDateClick(dateStr)}
              >
                <span>{date.getDate()}</span>
                {dotColor && <span className={`absolute bottom-1 w-1 h-1 rounded-full ${dotColor}`}></span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-4 px-2">
        <div className="flex items-center gap-2">
          <button onClick={handlePrev} className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 shadow-sm">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={handleNext} className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 shadow-sm">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button onClick={(e) => { e.preventDefault(); setBaseMonth(new Date()); }} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 text-gray-700 shadow-sm">
            Aujourd'hui
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] md:text-sm text-gray-600">
           <span className="flex items-center gap-1"><span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-hatched-gray border border-gray-300"></span> Week-ends</span>
           <span className="flex items-center gap-1"><span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-orange-400"></span> Sélection</span>
           <span className="flex items-center gap-1 lg:ml-2"><span className="w-2 h-2 rounded-full bg-orange-500"></span> En attente</span>
           <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Approuvé</span>
           <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> Refusé</span>
        </div>
      </div>
      <div className="flex gap-4 overflow-hidden">
        {renderMonth(0, 'block')}
        {renderMonth(1, 'hidden md:block')}
        {renderMonth(2, 'hidden lg:block')}
        {renderMonth(3, 'hidden xl:block')}
      </div>
    </div>
  );
};

export default InteractiveCalendar;
